import { useMutation, useQueryClient } from "react-query";
import { loggedInApiSingleConcurrentCall } from "src/api/axiosClient/userApi";
import { z } from "zod";
import {
    PostSchema,
    SsqPostCommentSchema,
    UnitConversion,
    SsqPostAttachmentSchema,
    sanitizeEmptyArray,
} from "src/api/client-api/post/postSchema";
import { calculationFailureMessage, classifyCalculationFailure } from "src/api/client-api/post/calculationFailure";
import { getOneIngredientKey } from "../ingredients/useGetOneIngredient";
import { getOneProductKey } from "../products/useGetOneProduct";
import { useSnackbar } from "src/utils/useSnackbar";
import { useTranslation } from "react-i18next";
import { getOneActionPlanKey } from "../ActionPlan/ActionPlan/useGetOneActionPlan";
import { getCompanyScoresHeadingsApiKey } from "src/pages/Company/Scores/CompanyScoreContextProvider";
import { getOrganizationPostApiKey } from "../post/useGetOrganizationPost";
import { getOnePostKey } from "../post/useGetOnePost";
import { getIngredientPostKey } from "../post/useGetIngredientPost";
import { getIngredientPostsKey } from "../post/useGetIngredientPosts";
import { getIngredientScoresKey } from "../olapScores/useGetIngredientScores";
import { getProductPostKey } from "../post/useGetProductPost";
import { getProductScoresKey } from "../olapScores/useGetProductScores";

export type CalculationType = "action" | "questionnaire" | "formula";

type SsqPostAttachment = {
    id: string;
    attachmentPath: string;
    createdAt: string;
};

export type UpdateProperty = {
    postId: number;
    propertyId: number;
    parentPropertyId?: number | undefined;
    value: number | string;
    position?: number;
    isGenericValue?: boolean | null;
    calculationType?: CalculationType;
    dataListOptionCodes?: string[] | null;
    ssqVersionStructureId?: number | null;
    postToUpdate?: number;
    ssqPostAttachments?: SsqPostAttachment[];
    doForce?: boolean;
    unitConversion?: UnitConversion;
};

export const updatePropertyFn = async (options: UpdateProperty) => {
    const {
        postId,
        propertyId,
        value,
        isGenericValue = null,
        position = 0,
        calculationType = "questionnaire",
        dataListOptionCodes = null,
        ssqVersionStructureId,
        postToUpdate,
        unitConversion,
    } = options;

    return await loggedInApiSingleConcurrentCall.post(
        `/api/ssq-posts/${postId}/property/${propertyId}/calculate`,
        {
            value: value,
            position: position,
            isGenericValue: isGenericValue,
            calculationType: calculationType,
            dataListOptionCodes: dataListOptionCodes,
            ...(ssqVersionStructureId != null ? { ssqVersionStructureId } : {}),
            ...(postToUpdate ? { postToUpdate } : {}),
            ...(unitConversion ? { unitConversion } : {}),
        },
        {
            headers: {
                Accept: "application/json",
            },
        },
    );
};

export const useUpdateProperty = () => {
    const queryClient = useQueryClient();
    const snackbar = useSnackbar();
    const { t } = useTranslation();

    const query = useMutation({
        mutationFn: updatePropertyFn,
        onSuccess: (result) => {
            queryClient.invalidateQueries([getCompanyScoresHeadingsApiKey]);
            queryClient.invalidateQueries([getOneIngredientKey]);
            queryClient.invalidateQueries([getIngredientPostKey]);
            queryClient.invalidateQueries([getIngredientPostsKey]);
            queryClient.invalidateQueries([getIngredientScoresKey]);
            queryClient.invalidateQueries([getOneProductKey]);
            queryClient.invalidateQueries([getProductPostKey]);
            queryClient.invalidateQueries([getProductScoresKey]);
            queryClient.invalidateQueries([getOneActionPlanKey]);
            queryClient.invalidateQueries([getOrganizationPostApiKey]);
            queryClient.invalidateQueries([getOnePostKey]);

            console.info("CALCULATION RESULT:", result.data);
        },
        onError: (error) => {
            snackbar.forceClose();
            snackbar.setSnackbarProps({
                type: "error",
                message: calculationFailureMessage(classifyCalculationFailure(error), t),
                forceAutoHideInMilisecond: 10000,
            });
        },
    });

    return query;
};

export const PostElementWriteSchema = z.object({
    id: z.number().optional(),
    value: z.string(),
    position: z.number(),
    ssqPropertyId: z.number().optional(),
    name: z.string().optional(),
    unit: z.string().optional(),
    isGenericValue: z.boolean().optional(),
    ssqHeadingId: z.number().optional(),
    dataListOptionCodes: z.preprocess(sanitizeEmptyArray, z.array(z.string()).optional()),
    ssqPostComments: z.preprocess(sanitizeEmptyArray, SsqPostCommentSchema.array().optional()),
    ssqPostAttachments: z.preprocess(sanitizeEmptyArray, z.array(SsqPostAttachmentSchema).optional()),
});

export type PostElementWrite = z.infer<typeof PostElementWriteSchema>;

export const PostWithWriteableElementsSchema = PostSchema.extend({
    elements: z.preprocess(sanitizeEmptyArray, PostElementWriteSchema.array().optional()),
});

export type PostWithWriteableElements = z.infer<typeof PostWithWriteableElementsSchema>;
